import React from "react";
import { graphql } from "gatsby";
import BaseLayout from "../../layout/base/BaseLayout";
import { Box, Button, ButtonBase, Stack, Typography } from "@mui/material";
import { GatsbyImage } from "gatsby-plugin-image";
import BgImg from "../../images/RegistartionBg.png";
import { ArrowBack } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { get } from "lodash";
import Seo from "../../components/Seo";
import { trackReportDownload } from "../../helpers/analytics";
import QueryPreservingLink from "../../components/QueryPreservingLink";
import useQueryPreservingNavigate from "../../helpers/useQueryPreservingNavigate";

const Report = ({
  data: {
    report: {
      data: { title, published, cover, content, description },
    },
  },
  location,
}) => {
  const isLoggedIn = useSelector((state) => Boolean(state.user.userId));
  const authInit = useSelector((state) => state.base.authInit);
  const navigate = useQueryPreservingNavigate();

  const onDownload = () => {
    if (!isLoggedIn)
      return navigate("/sign_in", {
        state: { redirect: location.pathname },
      });

    trackReportDownload(title.text);

    window.open(content.url, "_blank");
  };

  return (
    <BaseLayout location={location} page={title.text}>
      <Stack>
        <Stack
          sx={{
            padding: "46px 106px 0",
            backgroundImage: `url("${BgImg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            height: { xs: "100px", md: "246px" },
            alignItems: "start",
          }}
        >
          <Button
            component={QueryPreservingLink}
            to="/resources/reports"
            size="small"
            sx={{ color: "white" }}
            startIcon={<ArrowBack />}
          >
            back to reports
          </Button>
        </Stack>

        <Stack
          sx={{
            maxWidth: "1060px",
            width: "100%",
            alignSelf: "center",
            marginTop: { xs: "0", sm: "27px" },
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }}>
            <Stack
              sx={{ flex: 1, position: "relative", p: { xs: "0", md: "12px" } }}
            >
              <Box
                component={GatsbyImage}
                image={cover.gatsbyImageData}
                alt={cover.alt || "Report Image"}
                sx={{
                  top: { xs: "0", md: "-84px" },
                }}
              />
            </Stack>

            <Stack sx={{ flex: 1, p: "12px" }}>
              <Typography
                sx={{
                  fontSize: "18px",
                  color: "#A2AAB5",
                }}
              >
                {published}
              </Typography>

              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: "30px",
                  color: "#2a149a",
                  marginTop: "6px",
                }}
              >
                {title.text}
              </Typography>

              <Box
                component={ButtonBase}
                disabled={!authInit}
                onClick={onDownload}
                sx={{
                  display: "flex",
                  maxWidth: "260px",
                  width: "100%",
                  height: "75px",
                  borderRadius: "10px",
                  boxShadow: "0 15px 25px 0 rgba(106, 39, 232, 0.2)",
                  backgroundColor: "#6a27e8",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "bold",
                  justifyContent: "center",
                  alignItems: "center",
                  textDecoration: "none",
                  marginTop: "16px",
                  cursor: "pointer",
                }}
              >
                DOWNLOAD
              </Box>
            </Stack>
          </Stack>

          <Stack
            sx={{
              padding: { xs: "0 25px", md: "0 160px" },
            }}
          >
            <Box
              sx={{
                fontSize: "14px",
                color: "#626972",
                "& h1,h2,h3,h4,h5,h6": {
                  margin: "8px 0",
                  fontSize: { xs: "14px", md: "18px" },
                  fontWeight: "bold",
                  color: "#6a27e8",
                },
                "& p": {
                  mb: "15px",
                },
                "& ul, ol": {
                  pl: "40px",
                  mb: "15px",
                },
                "& a": {
                  color: "#6a27e8",
                },
              }}
              dangerouslySetInnerHTML={{ __html: description.html }}
            />
          </Stack>
        </Stack>
      </Stack>
    </BaseLayout>
  );
};

export default Report;

export const Head = (queryResult) => {
  const meta = get(queryResult, ["data", "prismicPage", "data", "meta"], {});

  return <Seo meta={meta} />;
};

export const pageQuery = graphql`
  query ReportPageQuery($id: String!) {
    report: prismicReport(id: { eq: $id }) {
      data {
        title {
          text
        }
        published(formatString: "MMMM DD, YYYY")
        cover {
          gatsbyImageData(layout: FULL_WIDTH)
        }
        content {
          url
        }
        description {
          html
        }
      }
    }
  }
`;
